import type { GapRowType, HiringMetricKey, MetricRowKey } from "../types";
import { AMBER, BLUE, EMERALD, PURPLE, ROSE } from "./styleTokens";

/**
 * One breakdown row of the Recurring Gaps grid. Each pairs an actual-data grid with a tone so the row
 * reads at a glance. `MetricRowKey` (wider than `HiringMetricKey` — `agency` is a row but not a count
 * lever) lives in `types.ts` so the persisted "which rows show" preference can be typed there too.
 */
export type MetricRow = {
	key: MetricRowKey;
	labelKey: string;
	color: string;
	bg: string;
	// Display-only rows (agency) have no Count toggle and never feed the Missing gap.
	countable: boolean;
};

// Display order + color of the actual-data breakdown rows (between Needed and Recurring). `countable`
// rows have a Count toggle and feed the Missing gap; Agency is display-only (DAT-248). Shared by the grid
// and its Excel download, so the file lists the same rows in the same order.
export const METRIC_ROWS: MetricRow[] = [
	{ key: "structural_ot", labelKey: "hiring.gaps.structuralOt", color: ROSE[600], bg: `${ROSE[50]}99`, countable: true },
	{ key: "internal_pickup_ot", labelKey: "hiring.gaps.internalPickupOt", color: AMBER[700], bg: `${AMBER[50]}99`, countable: true },
	{ key: "internal_pickup_non_ot", labelKey: "hiring.gaps.internalPickupNonOt", color: EMERALD[600], bg: `${EMERALD[50]}99`, countable: true },
	{ key: "agency", labelKey: "hiring.gaps.agency", color: BLUE[600], bg: `${BLUE[50]}99`, countable: false },
	{ key: "absences", labelKey: "hiring.gaps.absences", color: PURPLE[600], bg: `${PURPLE[50]}99`, countable: true },
];

/** The translation key naming a row of a shift block — the grid's left column and the download's. */
export const gapsRowLabelKey = (rowType: GapRowType): string => {
	if (rowType === "needed") return "hiring.gaps.neededShort";
	if (rowType === "recurring") return "hiring.gaps.recurring";
	if (rowType === "missing") return "hiring.gaps.missing";
	return metricRowOf(rowType).labelKey;
};

/** The breakdown row for a metric key. Every `MetricRowKey` has one, so a miss is a broken `METRIC_ROWS`. */
export const metricRowOf = (key: MetricRowKey): MetricRow => {
	const row = METRIC_ROWS.find((metric) => metric.key === key);
	if (!row) {
		throw new Error(`No breakdown row for metric ${key}`);
	}
	return row;
};

/**
 * A breakdown row shown but left out of Missing by its Count switch. The grid dims the whole row so it reads
 * as not counted; agency is display-only and never counted, so it is never "uncounted".
 */
export const isUncountedRow = (rowType: GapRowType, includeFlags: Record<HiringMetricKey, boolean>): boolean =>
	rowType !== "needed" && rowType !== "recurring" && rowType !== "missing" && rowType !== "agency" && !includeFlags[rowType];
